import { useState } from "react";
import { IconCheck, IconLock } from "@tabler/icons-react";
import Card from "../UI/Card";
import Button from "../UI/Button";
import Text from "../UI/Text";
import Title from "../UI/Title";
import Status from "../UI/Status";
import Callout from "../UI/Callout";
// @ts-expect-error pure js
import useLoader from "../../hooks/use-loader";
import usePennylane from "../../hooks/use-pennylane";
// @ts-expect-error pure js
import { classnames, handleAPIError } from "../../utils";
// @ts-expect-error pure js
import useAuth from "../../hooks/use-auth";
import ConnectPennylaneModal from "./ConnectPennylaneModal";
import type { ProfileBailleur } from "./types";
import styles from "./PennylaneCard.module.css";

interface PennylaneCardProps {
  profileBailleur: ProfileBailleur;
}

type Feedback = { type: "success" | "danger"; message: string };

export default function PennylaneCard({ profileBailleur }: PennylaneCardProps) {
  const { auth } = useAuth();
  const {
    pennylane,
    isLoading,
    connect,
    disconnect,
    mutate,
    getOauthAuthorizationUrl,
    disconnectOauth,
  } = usePennylane(profileBailleur.id);

  const [modalOpen, setModalOpen] = useState(false);
  const [feedback, setFeedback] = useState<Feedback | null>(null);

  async function redirectToOauth() {
    const url = await getOauthAuthorizationUrl();
    window.location.href = url;
  }

  const [isAuthorizing, authorizeOauth] = useLoader(async () => {
    setFeedback(null);
    try {
      await redirectToOauth();
    } catch (e) {
      setFeedback({ type: "danger", message: handleAPIError(e) });
    }
  });

  const [isDisconnecting, disconnectAccount] = useLoader(async () => {
    setFeedback(null);
    try {
      await disconnect();
      setFeedback({
        type: "success",
        message: "Déconnexion de Pennylane réussie",
      });
      mutate();
    } catch (e) {
      setFeedback({ type: "danger", message: handleAPIError(e) });
    }
  });

  const [isRevoking, revokeOauth] = useLoader(async () => {
    setFeedback(null);
    try {
      await disconnectOauth();
      setFeedback({
        type: "success",
        message: "Autorisation facturation électronique révoquée",
      });
      mutate();
    } catch (e) {
      setFeedback({ type: "danger", message: handleAPIError(e) });
    }
  });

  function handleConnectSuccess() {
    setModalOpen(false);
    setFeedback({
      type: "success",
      message:
        "Compte Pennylane créé. Vous pouvez maintenant autoriser la facturation électronique.",
    });
    mutate();
  }

  if (isLoading || !pennylane) {
    return (
      <Card>
        <Title variant="kilo">Pennylane</Title>
        <Text color="muted">Chargement...</Text>
      </Card>
    );
  }

  const isFullyDisconnected =
    !pennylane.connected && !pennylane.oauth_connected;

  return (
    <Card className={styles.card}>
      <div className={styles.header}>
        <div className={styles.logo} aria-hidden="true">
          P
        </div>
        <div>
          <Title variant="kilo">Pennylane</Title>
          <Text variant="deci" color="muted">
            Facturation électronique de vos loyers
          </Text>
        </div>
        {pennylane.oauth_connected ? (
          <Status
            className={styles.status}
            color="success"
            text="Facturation active"
          />
        ) : pennylane.connected ? (
          <Status
            className={styles.status}
            color="warning"
            text="Autorisation requise"
          />
        ) : (
          <Status
            className={styles.status}
            color="neutral"
            text="Non connecté"
          />
        )}
      </div>

      <Text color="muted">
        Connectez votre compte Pennylane pour envoyer vos factures de loyer en
        facturation électronique.
      </Text>

      <div className={styles.steps}>
        {pennylane.connected ? (
          <div className={classnames(styles.step, styles.done)}>
            <div className={styles.stepNum}>
              <IconCheck size={18} stroke={2.5} />
            </div>
            <div className={styles.stepBody}>
              <Text color="success" weight="bold">
                Compte Pennylane connecté
              </Text>
              <Text variant="deci" color="success">
                {pennylane.company_name
                  ? `Société « ${pennylane.company_name} »`
                  : pennylane.company_id
                  ? `ID Société ${pennylane.company_id}`
                  : "Société autorisée dans Pennylane"}
              </Text>
            </div>
            <Button
              type="button"
              variant="tertiary"
              onClick={disconnectAccount}
              isLoading={isDisconnecting}
            >
              Déconnecter
            </Button>
          </div>
        ) : (
          <div className={styles.step}>
            <div className={styles.stepNum}>1</div>
            <div className={styles.stepBody}>
              <Text weight="bold">Connectez votre compte Pennylane</Text>
              <Text variant="deci" color="muted">
                Vous serez redirigé vers Pennylane pour vous identifier en toute
                sécurité.
              </Text>
            </div>
            <Button
              type="button"
              variant="primary"
              onClick={() => setModalOpen(true)}
            >
              Connecter Pennylane
            </Button>
          </div>
        )}

        {pennylane.oauth_connected ? (
          <div className={classnames(styles.step, styles.done)}>
            <div className={styles.stepNum}>
              <IconCheck size={18} stroke={2.5} />
            </div>
            <div className={styles.stepBody}>
              <Text color="success" weight="bold">
                Facturation électronique autorisée
              </Text>
              <Text variant="deci" color="success">
                Vos factures de loyer sont transmises à Pennylane, Plateforme
                Agréée.
              </Text>
            </div>
            <Button
              type="button"
              variant="tertiary"
              onClick={revokeOauth}
              isLoading={isRevoking}
            >
              Révoquer l&apos;autorisation
            </Button>
          </div>
        ) : isFullyDisconnected ? (
          <div className={classnames(styles.step, styles.locked)}>
            <div className={styles.stepNum}>2</div>
            <div className={styles.stepBody}>
              <Text color="muted" weight="bold">
                Autorisez la facturation électronique
              </Text>
              <Text variant="deci" color="muted">
                Qalimo transmettra vos factures de loyer à Pennylane en tant que
                Plateforme Agréée.
              </Text>
            </div>
            <Text variant="deci" color="muted" className={styles.lockHint}>
              <IconLock size={16} stroke={1.5} />
              Après connexion
            </Text>
          </div>
        ) : (
          <div className={styles.step}>
            <div className={styles.stepNum}>2</div>
            <div className={styles.stepBody}>
              <Text weight="bold">Autorisez la facturation électronique</Text>
              <Text variant="deci" color="muted">
                Qalimo transmettra vos factures de loyer à Pennylane en tant que
                Plateforme Agréée.
              </Text>
            </div>
            <Button
              type="button"
              variant="primary"
              onClick={authorizeOauth}
              isLoading={isAuthorizing}
            >
              Autoriser
            </Button>
          </div>
        )}
      </div>

      {pennylane.oauth_connected ? (
        <Callout variant="success">
          Tout est en place : les factures de vos locataires de type
          «&nbsp;Facture&nbsp;» partent automatiquement en facturation
          électronique.
        </Callout>
      ) : (
        <Callout variant="info">
          Concerne vos locataires de type «&nbsp;Facture&nbsp;». La connexion
          est sécurisée et révocable à tout moment.
        </Callout>
      )}

      {feedback && (
        <Callout variant={feedback.type}>{feedback.message}</Callout>
      )}

      <ConnectPennylaneModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        defaultEmail={auth.mail || ""}
        onConnect={connect}
        onAuthorize={redirectToOauth}
        onSuccess={handleConnectSuccess}
      />
    </Card>
  );
}
